import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Mail, Lock, Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import PublicHeader from '../components/PublicHeader';
import PublicFooter from '../components/PublicFooter';

const UniversalLogin = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  
  const [form, setForm] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!form.email || !form.email.includes('@')) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!form.password) {
      newErrors.password = 'Please enter your password';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    
    setLoading(true);
    try {
      // Use the auth context login function which handles API call and state
      const result = await login(form.email.trim().toLowerCase(), form.password);
      
      if (result.success) {
        toast.success('Login successful!');
        
        // Redirect to tenant dashboard using the returned redirect_url
        if (result.redirect_url) {
          navigate(result.redirect_url);
        } else if (result.tenant_slug) {
          navigate(`/${result.tenant_slug}/dashboard`);
        } else {
          // Super admin or no tenant - redirect to admin
          navigate('/admin');
        }
      } else {
        toast.error(result.error || 'Invalid email or password');
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Invalid email or password';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Header - Shared component with logo from CMS */}
      <PublicHeader />

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl bg-white border-slate-200">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl text-[#0a192f]">Sign In to Your Account</CardTitle>
            <CardDescription className="text-[#0a192f]/70">Enter your credentials to access your dashboard</CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#0a192f]">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-[#0a192f]/50" />
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="Enter your email"
                    className={`pl-10 bg-white border-[#0a192f]/30 text-[#0a192f] placeholder:text-[#0a192f]/40 focus:border-[#0a192f] focus:ring-[#0a192f]/20 ${errors.email ? 'border-red-500' : ''}`}
                    data-testid="login-email"
                  />
                </div>
                {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#0a192f]">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-[#0a192f]/50" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="Enter your password"
                    className={`pl-10 pr-10 bg-white border-[#0a192f]/30 text-[#0a192f] placeholder:text-[#0a192f]/40 focus:border-[#0a192f] focus:ring-[#0a192f]/20 ${errors.password ? 'border-red-500' : ''}`}
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3 text-[#0a192f]/50 hover:text-[#0a192f]"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.password && <p className="text-xs text-red-500">{errors.password}</p>}
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="remember"
                    checked={rememberMe}
                    onCheckedChange={setRememberMe}
                    className="border-[#0a192f]/30 data-[state=checked]:bg-gradient-to-r data-[state=checked]:from-cyan-500 data-[state=checked]:to-teal-500"
                  />
                  <label htmlFor="remember" className="text-sm text-[#0a192f]/70">Remember me</label>
                </div>
                <Link to="/forgot-password" className="text-sm text-cyan-600 hover:text-teal-600 font-medium">
                  Forgot Password?
                </Link>
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-semibold shadow-lg shadow-cyan-500/25"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                Sign In
              </Button>

              <p className="text-center text-sm text-[#0a192f]/70">
                Don't have an account?{' '}
                <Link to="/register" className="text-cyan-600 hover:text-teal-600 font-medium">Create Account</Link>
              </p>
            </form>

            <div className="mt-6 pt-6 border-t border-[#0a192f]/10">
              <p className="text-center text-sm text-[#0a192f]/50">
                Prefer your organization's direct login?<br />
                Go to: <span className="text-cyan-600">ontrof.com/[your-org-slug]/login</span>
              </p>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Footer - Shared component with logo from CMS */}
      <PublicFooter />
    </div>
  );
};

export default UniversalLogin;
